export interface RestParam {
  key: string;
  value: string;
}

export type QueryRiskLevel = "harmless" | "normal" | "dangerous";

export const DEFAULT_QUERY_RISK_LEVEL: QueryRiskLevel = "normal";

export function normalizeQueryRiskLevel(input: unknown): QueryRiskLevel {
  if (input === "harmless" || input === "normal" || input === "dangerous") {
    return input;
  }
  return DEFAULT_QUERY_RISK_LEVEL;
}

export function normalizeRemarks(input: unknown): string {
  return typeof input === "string" ? input : "";
}

export function normalizeRestParams(input: unknown): RestParam[] {
  if (!Array.isArray(input)) return [];

  const result: RestParam[] = [];
  for (const entry of input) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const key = typeof record.key === "string" ? record.key : "";
    const value = typeof record.value === "string" ? record.value : record.value == null ? "" : String(record.value);
    result.push({ key, value });
  }
  return result;
}

export function parseRestPath(rawPath: string | undefined): {
  path: string;
  params: RestParam[];
} {
  const trimmed = rawPath?.trim() || "/";
  const queryIndex = trimmed.indexOf("?");
  if (queryIndex === -1) {
    return { path: trimmed || "/", params: [] };
  }

  const path = trimmed.slice(0, queryIndex) || "/";
  const params: RestParam[] = [];
  const search = new URLSearchParams(trimmed.slice(queryIndex + 1));
  search.forEach((value, key) => {
    params.push({ key, value });
  });
  return { path, params };
}

export function buildRestPath(path: string | undefined, params: RestParam[] | undefined): string {
  const trimmedPath = path?.trim() || "/";
  const normalizedParams = normalizeRestParams(params).filter((entry) => entry.key.trim());
  if (normalizedParams.length === 0) {
    return trimmedPath;
  }

  const search = new URLSearchParams();
  for (const entry of normalizedParams) {
    search.append(entry.key, entry.value);
  }
  return `${trimmedPath}?${search.toString()}`;
}

export function normalizeRestPathAndParams(
  rawPath: string | undefined,
  rawParams: unknown,
): {
  restPath: string;
  restParams: RestParam[];
} {
  const parsed = parseRestPath(rawPath);
  const explicitParams = normalizeRestParams(rawParams);
  // If the path carried a query string it represents the most recent edit
  // (URL-bar authoring), so those params win. Otherwise fall back to the
  // separate restParams field (grid edits, CLI, remote sync).
  return {
    restPath: parsed.path,
    restParams: parsed.params.length > 0 ? parsed.params : explicitParams,
  };
}

export function riskLevelMessage(riskLevel: QueryRiskLevel): string | null {
  switch (riskLevel) {
    case "normal":
      return "This request is marked normal. Make sure you understand the impact before you run it.";
    case "dangerous":
      return "This request is marked dangerous. Ask the user before you run it.";
    default:
      return null;
  }
}
